import { ref } from 'vue'
import { useRouter } from 'vue-router'

export function useCoupon() {
  const router = useRouter()
  const couponCode = ref('')
  const couponLoading = ref(false)
  const couponResult = ref(null)

  const validateCoupon = async (couponType = 'coin_purchase') => {
    if (!couponCode.value.trim()) return

    couponLoading.value = true
    couponResult.value = null

    try {
      const { couponsApi } = await import('@/api/coupons')
      const result = await couponsApi.validateCoupon(
        couponCode.value.trim().toUpperCase(),
        couponType,
        0,
      )

      // Handle redirects based on coupon type
      if (couponType === 'coin_purchase' && (result.coupon.type === 'free_team_pro' || result.coupon.type === 'team_upgrade')) {
        router.push({
          name: 'sales',
          query: {
            couponCode: couponCode.value.trim().toUpperCase()
          }
        })
        return
      }

      if (couponType === 'team_upgrade' && (result.coupon.type === 'free_coins' || result.coupon.type === 'user_related')) {
        router.push({
          name: 'purchase',
          query: {
            couponCode: couponCode.value.trim().toUpperCase()
          }
        })
        return
      }

      couponResult.value = {
        success: true,
        data: result,
      }
    } catch (error) {
      couponResult.value = {
        success: false,
        error: error.response?.data?.message || 'Gutschein konnte nicht validiert werden',
      }
    } finally {
      couponLoading.value = false
    }
  }

  const getCouponDescription = (coupon) => {
    if (coupon.type === 'free_coins') {
      const coinsRaw = coupon.coins ?? coupon.value ?? coupon.discountAmount ?? 0
      const coinsNumber = Number(coinsRaw)
      const coins = Number.isFinite(coinsNumber)
        ? Number.isInteger(coinsNumber)
          ? coinsNumber
          : coinsNumber.toFixed(2)
        : coinsRaw || 0
      return `Kostenlose ${coins} Quittungen`
    }
    if (coupon.type === 'user_related') {
      return `Benutzer-Gutschein: ${coupon.description || 'Rabatt verfügbar'}`
    }
    if (coupon.type === 'free_team_pro') {
      const planText = coupon.teamProPlan === 'ultimate' ? 'Ultimate' : 'Pro'
      const durationUnit = coupon.teamProDurationUnit || 'months'
      const durationLabels = {
        days: 'Tage',
        months: 'Monate',
        years: 'Jahre',
      }
      const durationLabel = durationLabels[durationUnit] || durationUnit
      const durationText =
        coupon.teamProDuration === 1
          ? `1 ${durationLabel.slice(0, -1)}`
          : `${coupon.teamProDuration} ${durationLabel}`
      const coinsText = coupon.teamProCoins > 0 ? ` + ${coupon.teamProCoins} Quittungen (Gesamt)` : ''
      return `Kostenloses Team ${planText} für ${durationText}${coinsText}`
    }
    return coupon.description || 'Gutschein'
  }

  const onCouponCodeChange = (onRecalculate) => {
    if (!couponCode.value.trim()) {
      couponResult.value = null
      if (onRecalculate) {
        onRecalculate()
      }
    }
  }

  return {
    couponCode,
    couponLoading,
    couponResult,
    validateCoupon,
    getCouponDescription,
    onCouponCodeChange
  }
}
